/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Stack, Box, Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import useLoginFields from '../forms/useLoginFields'
import Field from '../field'

const LoginFields = ({
    form,
    handleForgotPasswordClick,
    prefix = 'login_',
    hideEmail = false,
    hidePassword = false,
    isFirstInputFocus = false
}) => {
    const fields = useLoginFields({form, prefix})
    const emailRef = useRef()
    const passwordRef = useRef()

    useEffect(() => {
        if (!isFirstInputFocus) return

        if (!hideEmail && emailRef.current) {
            emailRef.current.focus()
            return
        }

        if (!hidePassword && passwordRef.current) {
            passwordRef.current.focus()
        }
    }, [])

    return (
        <Stack spacing={24}>
            {!hideEmail && <Field {...fields.email} inputRef={emailRef} />}
            {!hidePassword && (
                <Stack spacing={24}>
                    <Field {...fields.password} inputRef={passwordRef} />
                    {handleForgotPasswordClick && (
                        <Box>
                            <Button variant="link" onClick={handleForgotPasswordClick}>
                                <FormattedMessage
                                    defaultMessage="Forgot Password?"
                                    id="login_form.link.forgot_password"
                                />
                            </Button>
                        </Box>
                    )}
                    <Box></Box>
                </Stack>
            )}
        </Stack>
    )
}

LoginFields.propTypes = {
    handleForgotPasswordClick: PropTypes.func,

    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Optional configurations */
    hideEmail: PropTypes.bool,
    hidePassword: PropTypes.bool,

    /** Determines if the first input should be focused */
    isFirstInputFocus: PropTypes.bool
}

export default LoginFields
